/**
 * SubscriptionManagerCard — patient self-serve for native Square subscriptions.
 * Lists active subscriptions with pause / resume / cancel and a secure
 * "Update card" flow (re-tokenizes via Square's hosted fields).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { confirm as brandedConfirm } from "@/components/ui/confirm-dialog";
import { toast } from "sonner";
import { Repeat, Loader2, CreditCard, Pause, Play, XCircle, X, PiggyBank, Sparkles } from "lucide-react";
import SquareCardForm from "@/components/SquareCardForm";

const CADENCE_LABEL = { MONTHLY: "every month", QUARTERLY: "every 3 months" };
const STATUS_STYLE = {
  ACTIVE: "bg-emerald-100 text-emerald-700",
  PAUSED: "bg-amber-100 text-amber-700",
  CANCELED: "bg-slate-200 text-slate-600",
  DEACTIVATED: "bg-rose-100 text-rose-700",
};

export default function SubscriptionManagerCard() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null); // subscription being card-updated
  const [saveOffer, setSaveOffer] = useState(null); // { sub, offer } churn-saver

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await API.get("/subscriptions/mine");
      setSubs(Array.isArray(data) ? data : []);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (sub, action, opts) => {
    if (opts?.confirm) {
      const ok = await brandedConfirm(opts.confirm);
      if (!ok) return;
    }
    setBusy(`${sub.id}:${action}`);
    try {
      const { data } = await API.post(`/subscriptions/${sub.id}/${action}`);
      toast.success(data.message || "Done");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    } finally {
      setBusy(null);
    }
  };

  const doCancel = (sub) => act(sub, "cancel", { confirm: {
    title: "Cancel this subscription?",
    description: "It will remain active until the end of the current billing period, then stop renewing. You won't be charged again.",
    confirmText: "Cancel subscription", variant: "destructive",
  }});

  // Churn-saver: before cancelling, check if we can offer a cheaper plan.
  const startCancel = async (sub) => {
    setBusy(`${sub.id}:offer`);
    try {
      const { data } = await API.get(`/subscriptions/${sub.id}/downgrade-offer`);
      if (data?.available) { setSaveOffer({ sub, offer: data }); return; }
    } catch { /* fall through to normal cancel */ }
    finally { setBusy(null); }
    doCancel(sub);
  };

  const doDowngrade = async () => {
    if (!saveOffer) return;
    const { sub } = saveOffer;
    setBusy(`${sub.id}:downgrade`);
    try {
      const { data } = await API.post(`/subscriptions/${sub.id}/downgrade`);
      toast.success(data.message || "Switched to the Solo plan");
      setSaveOffer(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not switch plans");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6" data-testid="subscriptions-loading">
        <Loader2 className="w-4 h-4 animate-spin text-slate-400 inline mr-2" /> Loading subscriptions…
      </div>
    );
  }

  if (!subs.length) return null;

  return (
    <div className="bg-white rounded-2xl border border-purple-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] p-6 sm:p-8" data-testid="subscription-manager-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-sky-100 flex items-center justify-center">
          <Repeat className="w-5 h-5 text-sky-600" />
        </div>
        <div>
          <h2 className="text-xl font-semibold text-slate-900" style={{ fontFamily: "'Outfit', sans-serif" }}>My Subscriptions</h2>
          <p className="text-xs text-slate-500">Manage your auto-renewing medication plans.</p>
        </div>
      </div>

      <div className="space-y-3">
        {subs.map((s) => {
          const canceled = s.status === "CANCELED" || s.status === "DEACTIVATED";
          return (
            <div key={s.id} className="rounded-xl border border-slate-200 p-4" data-testid={`subscription-row-${s.id}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-semibold text-slate-900">{s.medication_name || s.tier_name}</p>
                    <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLE[s.status] || "bg-slate-100 text-slate-600"}`}>
                      {s.status}
                    </span>
                  </div>
                  <p className="text-sm text-slate-600 mt-0.5">
                    ${Number(s.amount).toFixed(2)} {CADENCE_LABEL[s.cadence] || ""}
                  </p>
                  {s.payment_failed && (
                    <p className="text-xs text-rose-600 mt-1">Last renewal failed — please update your card.</p>
                  )}
                  {s.pending_downgrade_tier && (
                    <p className="text-xs text-emerald-600 mt-1" data-testid={`pending-downgrade-${s.id}`}>
                      Switching to {s.pending_downgrade_tier_name || "Solo"} (${Number(s.pending_downgrade_amount).toFixed(2)}) next cycle.
                    </p>
                  )}
                </div>
                {!canceled && (
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" onClick={() => setEditing(s)} data-testid={`update-card-${s.id}`}>
                      <CreditCard className="w-3.5 h-3.5 mr-1.5" /> Update card
                    </Button>
                    {s.status === "PAUSED" ? (
                      <Button size="sm" variant="outline" disabled={busy === `${s.id}:resume`} onClick={() => act(s, "resume")} data-testid={`resume-${s.id}`}>
                        <Play className="w-3.5 h-3.5 mr-1.5" /> Resume
                      </Button>
                    ) : (
                      <Button size="sm" variant="outline" disabled={busy === `${s.id}:pause`} onClick={() => act(s, "pause")} data-testid={`pause-${s.id}`}>
                        <Pause className="w-3.5 h-3.5 mr-1.5" /> Pause
                      </Button>
                    )}
                    <Button
                      size="sm" variant="outline"
                      className="border-rose-300 text-rose-700 hover:bg-rose-50"
                      disabled={busy === `${s.id}:cancel` || busy === `${s.id}:offer`}
                      onClick={() => startCancel(s)}
                      data-testid={`cancel-${s.id}`}
                    >
                      <XCircle className="w-3.5 h-3.5 mr-1.5" /> Cancel
                    </Button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {editing && (
        <UpdateCardModal sub={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}

      {saveOffer && (
        <SaveOfferModal
          data={saveOffer}
          busy={busy === `${saveOffer.sub.id}:downgrade`}
          onDowngrade={doDowngrade}
          onCancelAnyway={() => { const s = saveOffer.sub; setSaveOffer(null); doCancel(s); }}
          onClose={() => setSaveOffer(null)}
        />
      )}
    </div>
  );
}

const CADENCE_SHORT = { MONTHLY: "/mo", QUARTERLY: "/3 mo" };

function SaveOfferModal({ data, busy, onDowngrade, onCancelAnyway, onClose }) {
  const { offer } = data;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="save-offer-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="bg-gradient-to-br from-sky-500 to-purple-600 px-6 py-5 text-white">
          <Sparkles className="w-6 h-6 mb-2" />
          <h3 className="text-lg font-semibold" style={{ fontFamily: "'Outfit', sans-serif" }}>Wait — keep your treatment for less</h3>
          <p className="text-sm text-white/90 mt-1">Before you cancel, switch to our cheaper Solo plan and stay on your medication.</p>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between rounded-xl border border-slate-200 p-3">
            <div>
              <p className="text-xs text-slate-400 line-through">{offer.current_tier_name}</p>
              <p className="text-lg font-bold text-slate-400 line-through">${Number(offer.current_amount).toFixed(2)}<span className="text-xs font-normal">{CADENCE_SHORT[offer.current_cadence] || ""}</span></p>
            </div>
            <PiggyBank className="w-6 h-6 text-emerald-500" />
            <div className="text-right">
              <p className="text-xs font-semibold text-emerald-600">{offer.tier_name}</p>
              <p className="text-lg font-bold text-slate-900">${Number(offer.amount).toFixed(2)}<span className="text-xs font-normal text-slate-500">{CADENCE_SHORT[offer.cadence] || ""}</span></p>
            </div>
          </div>
          <p className="text-xs text-slate-500">You&apos;ll keep your current plan&apos;s benefits through the end of this billing period, then move to the Solo plan automatically. No new charge today.</p>
          <Button onClick={onDowngrade} disabled={busy} className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700 text-white py-6" data-testid="save-offer-downgrade">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Switching…</> : `Switch to Solo & stay`}
          </Button>
          <button onClick={onCancelAnyway} disabled={busy} className="w-full text-sm text-slate-500 hover:text-rose-600 py-1" data-testid="save-offer-cancel-anyway">
            No thanks, cancel my subscription
          </button>
          <button onClick={onClose} disabled={busy} className="w-full text-xs text-slate-400 hover:text-slate-600" data-testid="save-offer-dismiss">
            Never mind, keep my current plan
          </button>
        </div>
      </div>
    </div>
  );
}

function UpdateCardModal({ sub, onClose, onSaved }) {
  const cardRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [contact, setContact] = useState({ given_name: "", family_name: "", postal_code: "" });
  const set = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const save = async () => {
    if (!contact.given_name.trim() || !contact.family_name.trim()) {
      toast.error("Enter the cardholder's name."); return;
    }
    setSaving(true);
    try {
      const nonce = await cardRef.current.tokenize({
        givenName: contact.given_name, familyName: contact.family_name,
        postalCode: contact.postal_code || undefined, countryCode: "US",
      });
      const { data } = await API.post(`/subscriptions/${sub.id}/update-card`, {
        nonce,
        billing_contact: { given_name: contact.given_name, family_name: contact.family_name, postal_code: contact.postal_code || null },
      });
      toast.success(data.message || "Card updated");
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Could not update the card.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4" data-testid="update-card-modal">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <h3 className="font-semibold text-slate-900">Update payment card</h3>
          {!saving && <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="update-card-close"><X className="w-5 h-5" /></button>}
        </div>
        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-slate-600">First name</label>
              <Input value={contact.given_name} onChange={set("given_name")} data-testid="update-first-name" />
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600">Last name</label>
              <Input value={contact.family_name} onChange={set("family_name")} data-testid="update-last-name" />
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600">ZIP</label>
            <Input value={contact.postal_code} onChange={set("postal_code")} data-testid="update-zip" />
          </div>
          <div>
            <label className="text-xs font-medium text-slate-600 mb-1 block">New card</label>
            <SquareCardForm ref={cardRef} />
          </div>
          <Button onClick={save} disabled={saving} className="w-full rounded-full bg-sky-600 hover:bg-sky-700 text-white py-6" data-testid="update-card-save">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : "Save new card"}
          </Button>
        </div>
      </div>
    </div>
  );
}
